import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { GetWarehouseLookupDocument } from "@/graphql/codegen/graphql"
import type { Lookup, LookupQueriesWarehouseAreasArgs, Query, WarehouseType } from "@/graphql/codegen/graphql"

interface UseLookupWarehousesReturn {
  warehouses: Lookup[]
  isFetching: boolean
  error?: CombinedError
}

interface UseLookupWarehousesParams {
  types?: WarehouseType[]
  limit?: number
}

const LIMIT = 100

export const useLookupWarehouses = ({
  types,
  limit = LIMIT,
}: UseLookupWarehousesParams = {}): UseLookupWarehousesReturn => {
  const [warehouses, setWarehouses] = useState<Lookup[]>([])
  const [offset, setOffset] = useState(0)

  const [{ data, fetching, error }] = useQuery<Pick<Query, "lookup">, LookupQueriesWarehouseAreasArgs>({
    query: GetWarehouseLookupDocument,
    variables: { input: { filter: { types, isActive: true }, limit, offset } },
  })

  useEffect(() => {
    if (data?.lookup.warehouseAreas.__typename === "WarehouseAreaLookupSuccess") {
      const paginatedWarehouses = data.lookup.warehouseAreas.items

      setWarehouses((prev: Lookup[]) => {
        const warehouses = offset === 0 ? [...paginatedWarehouses] : [...prev, ...paginatedWarehouses]
        warehouses.sort((a, b) => a.label.localeCompare(b.label))

        return warehouses
      })

      if (paginatedWarehouses.length === LIMIT) {
        setOffset((prevOffset) => prevOffset + LIMIT)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return {
    warehouses,
    isFetching: fetching,
    error,
  }
}
